import { type FC, type KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import cn from 'classnames';

import { Dialog } from './Dialog';
import { IconButton } from './IconButton';
import { LightboxImage } from './LightboxImage';
import type { PostMediaImage } from './PostMediaFrame';

interface ImageLightboxProps {
  images: PostMediaImage[];
  initialIndex: number;
  onClose: () => void;
}

const CONTROL_CLASS = 'absolute bg-white/90 shadow-sm hover:bg-white';

export const ImageLightbox: FC<ImageLightboxProps> = ({ images, initialIndex, onClose }) => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(initialIndex);
  const [isZoomed, setIsZoomed] = useState(false);
  const lastIndex = images.length - 1;
  const isFirstImage = index === 0;
  const isLastImage = index === lastIndex;
  const image = images[index];

  const showImage = (nextIndex: number) => {
    setIndex(nextIndex);
    setIsZoomed(false);
  };

  // While an image is zoomed the arrow keys pan it, whichever control holds focus.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (isZoomed) return;
    if (event.key === 'ArrowLeft' && !isFirstImage) showImage(index - 1);
    if (event.key === 'ArrowRight' && !isLastImage) showImage(index + 1);
  };

  return (
    <Dialog isOpen onClose={onClose} backdropClassName="bg-black/90" className="relative size-full">
      {/* biome-ignore lint/a11y/noStaticElementInteractions: arrow keys page through the images from whichever control holds focus */}
      <div className="size-full" data-testid="image-lightbox" onKeyDown={handleKeyDown}>
        <IconButton
          aria-label={t('community.post.lightbox.close')}
          className={cn(CONTROL_CLASS, 'right-2 top-2 z-10')}
          onClick={onClose}
        >
          <XMarkIcon className="size-4" />
        </IconButton>

        <LightboxImage
          key={image.id}
          image={image}
          isZoomed={isZoomed}
          onZoomChange={setIsZoomed}
          onDismiss={onClose}
        />

        {lastIndex > 0 && (
          <>
            <IconButton
              aria-label={t('community.post.carousel.previous_image')}
              className={cn(CONTROL_CLASS, 'left-2 top-1/2 -translate-y-1/2', { 'opacity-30': isFirstImage })}
              disabled={isFirstImage}
              onClick={() => showImage(index - 1)}
            >
              <ChevronLeftIcon className="size-4" />
            </IconButton>
            <IconButton
              aria-label={t('community.post.carousel.next_image')}
              className={cn(CONTROL_CLASS, 'right-2 top-1/2 -translate-y-1/2', { 'opacity-30': isLastImage })}
              disabled={isLastImage}
              onClick={() => showImage(index + 1)}
            >
              <ChevronRightIcon className="size-4" />
            </IconButton>
            <div
              aria-live="polite"
              className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs text-white"
            >
              {t('community.post.carousel.image_position', { current: index + 1, total: images.length })}
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};
